import { useDraggable } from "@dnd-kit/core";
import { DragIndicator, ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Icon, type SxProps, type Theme, Typography } from "@mui/material";
import { type FC, Fragment } from "react";
import { INK } from "../../../../theme/tokens";
import type { WeeksAndActiveState } from "../useGetMasterScheduleWeeksAndActiveState";
import { amountOfLeadingColumns, DEFAULT_VISIBLE_LOCATIONS, getSectionHeaderStyle, type Section, weekHeaderCardStyle } from "../utils";
import { GridDivider } from "./GridDivider";

type DraggableSectionHeaderProps = {
	section: Section;
	amountOfWeeks: number;
	isSynergyMS: boolean;
	weeksAndActiveState: WeeksAndActiveState[];
	isCollapsed: boolean;
	handleSectionCollapseToggle: (section: Section) => void;
	totalLocations: number;
	visibleLocations: number;
};

export const DraggableSectionHeader: FC<DraggableSectionHeaderProps> = ({
	section,
	amountOfWeeks,
	isSynergyMS,
	weeksAndActiveState,
	isCollapsed,
	handleSectionCollapseToggle,
	totalLocations,
	visibleLocations,
}) => {
	const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
		id: `section-drag-${section}`,
		data: {
			type: "section",
			sectionName: section,
		},
	});

	const style = transform
		? {
				transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
				opacity: isDragging ? 0.5 : 1,
			}
		: undefined;

	return (
		<Box
			component="button"
			sx={[getSectionHeaderStyle(amountOfWeeks, isSynergyMS), { border: "none", cursor: "pointer" }] as SxProps<Theme>}
			className="ms2-role-header"
			ref={setNodeRef}
			style={style}
			onClick={(_) => handleSectionCollapseToggle(section)}
			data-testid={`master-schedule-section-header-${section}`}
		>
			<Box sx={{ gridColumn: `span ${amountOfLeadingColumns}`, display: "flex", alignItems: "center", gap: 1 }}>
				<Box
					{...listeners}
					{...attributes}
					sx={{
						display: "flex",
						alignItems: "center",
						cursor: isDragging ? "grabbing" : "grab",
						"&:hover": { opacity: 0.7 },
					}}
				>
					<DragIndicator sx={{ fontSize: "18px", color: INK.SECONDARY }} />
				</Box>
				<Typography sx={{ fontSize: "inherit", fontWeight: "inherit", color: "inherit", lineHeight: "inherit" }}>{section}</Typography>
				{totalLocations > DEFAULT_VISIBLE_LOCATIONS && !isCollapsed && (
					<Typography component="span" sx={{ fontSize: "12px", fontWeight: "inherit", color: "inherit", opacity: 0.55, lineHeight: "inherit" }}>
						({visibleLocations}/{totalLocations})
					</Typography>
				)}
			</Box>
			{weeksAndActiveState.map((week, index) => {
				const isLast = index === weeksAndActiveState.length - 1;
				return (
					<Fragment key={`section_${week.firstDate.toISO()}`}>
						<Box
							data-week-index={index}
							className="ms2-grid-cell"
							sx={[weekHeaderCardStyle, { background: "none", justifyContent: "flex-end", height: "100%" }] as SxProps<Theme>}
						>
							{isLast && (
								<Icon sx={{ fontSize: "inherit", p: 0 }}>
									{isCollapsed ? <ExpandMore sx={{ fontSize: "18px" }} /> : <ExpandLess sx={{ fontSize: "18px" }} />}
								</Icon>
							)}
						</Box>
						{!isLast && <GridDivider />}
					</Fragment>
				);
			})}
		</Box>
	);
};
